<template>
  <div class="block">
    <div class="mix-set-list">
      <ul class="list-unstyled border-top">
        <li
          v-for="(setItem, index) in product.productPack.products"
          :id="`set-item-${index}`"
          :key="index"
          class="row c-pt-2 relative"
        >
          <!-- Icon -->
          <div class="col-auto static c-pr-0 c-sm:c-pr-2">
            <ButtonChevron
              :direction="[activeIndex === index ? 'u' : 'd']"
              :aria-expanded="activeIndex === index"
              :aria-controls="`set-answer-${index}`"
              :aria-labelledby="`set-question-${index}`"
              :btn-state="[activeIndex === index ? 'muffled' : 'success']"
              @click.prevent="toggleItem(index, false)"
            />
          </div>
          <!-- Item + Item detail -->
          <div class="col static c-self-center">
            <!-- item -->
            <p
              :id="`set-question-${index}`"
              class="c-my-2 h5"
            >
              {{ setItem.name }}
            </p>
            <!-- item detail -->
            <div
              v-show="activeIndex === index"
              :id="`set-answer-${index}`"
              class="relative z-1"
            >
              <p
                class="text-sm-c"
                v-html="setItem.composition"
              />
              <p
                v-if="setItem.allergens"
                class="text-sm-c"
                v-html="setItem.allergens"
              />
              <div
                v-if="setItem.nutrition"
                class="col-12 c-p-0"
              >
                <h2 class="c-mb-3 c-mt-5 text-primary text-left text-lg-c">
                  {{ t('nutritional_information') }}
                </h2>
                <table class="table-base table-striped border-bottom">
                  <tbody v-if="setItem.nutrition">
                    <tr>
                      <td class="c-px-2 c-py-2">
                        {{ t('energy_value') }}
                      </td>
                      <td class="font-bold whitespace-nowrap c-px-2 c-py-2">
                        {{ setItem.nutrition.energy }} kJ / {{ Math.round(setItem.nutrition.energy / 4.186) }} kcal
                      </td>
                    </tr>
                    <tr>
                      <td class="c-px-2 c-py-2">
                        {{ t('fats') }}
                      </td>
                      <td class="font-bold whitespace-nowrap c-px-2 c-py-2">
                        {{ setItem.nutrition.fats }} g
                      </td>
                    </tr>
                    <tr>
                      <td class="c-px-2 c-py-2">
                        {{ t('of_which_saturated_fatty_acids') }}
                      </td>
                      <td class="font-bold whitespace-nowrap c-px-2 c-py-2">
                        {{ setItem.nutrition.saturatedFats }} g
                      </td>
                    </tr>
                    <tr>
                      <td class="c-px-2 c-py-2">
                        {{ t('carbohydrates') }}
                      </td>
                      <td class="font-bold whitespace-nowrap c-px-2 c-py-2">
                        {{ setItem.nutrition.carbohydrates }} g
                      </td>
                    </tr>
                    <tr>
                      <td class="c-px-2 c-py-2">
                        {{ t('of_which_sugars') }}
                      </td>
                      <td class="font-bold whitespace-nowrap c-px-2 c-py-2">
                        {{ setItem.nutrition.sugars }} g
                      </td>
                    </tr>
                    <tr>
                      <td class="c-px-2 c-py-2">
                        {{ t('fiber') }}
                      </td>
                      <td class="font-bold whitespace-nowrap c-px-2 c-py-2">
                        {{ setItem.nutrition.fibre }} g
                      </td>
                    </tr>
                    <tr>
                      <td class="c-px-2 c-py-2">
                        {{ t('proteins') }}
                      </td>
                      <td class="font-bold whitespace-nowrap c-px-2 c-py-2">
                        {{ setItem.nutrition.proteins }} g
                      </td>
                    </tr>
                    <tr>
                      <td class="c-px-2 c-py-2">
                        {{ t('salt') }}
                      </td>
                      <td class="font-bold whitespace-nowrap c-px-2 c-py-2">
                        {{ setItem.nutrition.salt }} g
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <!-- Line -->
          <div class="col-12 c-pt-2">
            <hr class="c-m-0 border-primary opacity-20">
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { defineAsyncComponent, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const ButtonChevron = defineAsyncComponent(() => import('@/components/buttons/ButtonChevron.vue'))

defineProps({
  product: {
    type: Object,
    required: true,
  },
})

const { t } = useI18n()

const activeIndex = ref()

const toggleItem = (index, onScroll) => {
  activeIndex.value = activeIndex.value === index ? undefined : index
  if (onScroll) {
    const targetElement = document.querySelector(`#set-item-${index}`)
    const targetOffsetTop = targetElement.getBoundingClientRect().top + document.documentElement.scrollTop - 200
    window.scrollTo({ top: targetOffsetTop, behavior: 'smooth' })
  }
}
</script>

<i18n>
  {
    "cs": {
    "carbohydrates": "Sacharidy",
    "energy_value": "Energetická hodnota",
    "fats": "Tuky",
    "fiber": "Vláknina",
    "nutritional_information": "Výživové údaje na 100 g",
    "of_which_saturated_fatty_acids": "z toho nasycené mastné kyseliny",
    "of_which_sugars": "z toho cukry",
    "proteins": "Bílkoviny",
    "salt": "Sůl"
},
    "sk": {
    "carbohydrates": "Sacharidy",
    "energy_value": "Energia",
    "fats": "Tuky",
    "fiber": "Vláknina",
    "nutritional_information": "Výživové údaje na 100 g",
    "of_which_saturated_fatty_acids": "z toho nasýtené mastné kyseliny",
    "of_which_sugars": "z toho cukry",
    "proteins": "Bielkoviny",
    "salt": "Soľ"
},
    "pl": {
    "carbohydrates": "Węglowodany",
    "energy_value": "Wartość energetyczna",
    "fats": "Tłuszcz",
    "fiber": "Błonnik",
    "nutritional_information": "Wartości odżywcze w 100 g",
    "of_which_saturated_fatty_acids": "w tym kwasy tłuszczowe nasycone",
    "of_which_sugars": "w tym cukry",
    "proteins": "Białko",
    "salt": "Sól"
},
    "hu": {
    "carbohydrates": "Szénhidrátok",
    "energy_value": "Energiatartalom",
    "fats": "Zsírok",
    "fiber": "Rostok",
    "nutritional_information": "Tápértékadatok 100 g",
    "of_which_saturated_fatty_acids": "ebből telített zsírsavak",
    "of_which_sugars": "ebből cukror",
    "proteins": "Fehérjék",
    "salt": "Só"
},
    "de": {
    "carbohydrates": "Kohlenhydrate",
    "energy_value": "Energiewert",
    "fats": "Fette",
    "fiber": "Ballaststoffe",
    "nutritional_information": "Nährwerte pro 100 g",
    "of_which_saturated_fatty_acids": "davon gesättigte Fettsäuren",
    "of_which_sugars": "davon Zucker",
    "proteins": "Eiweiß",
    "salt": "Salz"
},
    "at": {
    "carbohydrates": "Kohlenhydrate",
    "energy_value": "Energiewert",
    "fats": "Fette",
    "fiber": "Ballaststoffe",
    "nutritional_information": "Nährwerte pro 100 g",
    "of_which_saturated_fatty_acids": "davon gesättigte Fettsäuren",
    "of_which_sugars": "davon Zucker",
    "proteins": "Eiweiß",
    "salt": "Salz"
},
    "ro": {
    "carbohydrates": "Carbohidrați",
    "energy_value": "Valoare energetică",
    "fats": "Grăsimi",
    "fiber": "Fibre",
    "nutritional_information": "Informații nutriționale 100 g",
    "of_which_saturated_fatty_acids": "din care acizi grași saturați",
    "of_which_sugars": "din care zaharuri",
    "proteins": "Proteine",
    "salt": "Sare"
},
    "fr": {
    "carbohydrates": "Glucides",
    "energy_value": "Valeur énergétique",
    "fats": "Matières grasses",
    "fiber": "Fibres alimentaires",
    "nutritional_information": "Valeurs nutritionnelles pour 100 g",
    "of_which_saturated_fatty_acids": "dont acides gras saturés",
    "of_which_sugars": "dont sucres",
    "proteins": "Protéines",
    "salt": "Sel"
},
    "it": {
    "carbohydrates": "Carboidrati",
    "energy_value": "Energia",
    "fats": "Grassi",
    "fiber": "Fibre",
    "nutritional_information": "Valori nutrizionali per 100 g",
    "of_which_saturated_fatty_acids": "di cui acidi grassi saturi",
    "of_which_sugars": "di cui zuccheri",
    "proteins": "Proteine",
    "salt": "Sale"
}
  }
</i18n>
